import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import type { TFunction } from "i18next";
import * as SliderPrimitive from "@radix-ui/react-slider";
import { ChevronDown, X } from "lucide-react";
import { cn } from "@/ui/lib/utils";
import { Button } from "@/ui/components/ui/button";
import { Input } from "@/ui/components/ui/input";
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/ui/components/ui/dropdown-menu";
import { Popover, PopoverContent, PopoverTrigger } from "@/ui/components/ui/popover";
import { useGetClassificationTaxonomy } from "@/ml-exports/api/queries/taxonomy.queries";
import { ALERT_TYPES, AlertStatus, alertTypeLabel, type Alert } from "@/alerts/types/machine-alert";
import type { LocationAlertFilters } from "@/alerts/hooks/useLocationAlerts";
import { formatRelativeTime } from "@/alerts/utils/relative-time";
import { formatNumber } from "@/shared/lib/i18n/formatters.ts";

/**
 * Location-aggregate alert feed for the `/map` panel — mirrors the Alerts page
 * visual (filter bar on top + card rows), but driven by real alerts and keyed
 * on the real backend status. Clicking a row opens the global real alert detail
 * modal (`openReal`), which handles images + Resolve/False.
 *
 * The list is windowed (fixed-height virtualization) so a location with
 * thousands of alerts renders only the visible rows.
 */

const ROW_HEIGHT = 44; // px — must match the row's fixed height below
const OVERSCAN = 6;

type StatusLabelKey = "pending" | "resolved" | "ignored";

const statusBadge: Record<
  AlertStatus,
  { dot: string; pill: string; solid: string; labelKey: StatusLabelKey }
> = {
  [AlertStatus.Pending]: {
    dot: "bg-amber-500",
    pill: "bg-amber-500/10 text-amber-600 dark:text-amber-400",
    solid: "bg-amber-500 text-white",
    labelKey: "pending",
  },
  [AlertStatus.Resolved]: {
    dot: "bg-green-500",
    pill: "bg-green-500/10 text-green-600 dark:text-green-400",
    solid: "bg-green-500 text-white",
    labelKey: "resolved",
  },
  [AlertStatus.Ignored]: {
    dot: "bg-gray-400",
    pill: "bg-gray-500/10 text-gray-600 dark:text-gray-400",
    solid: "bg-gray-400 text-white",
    labelKey: "ignored",
  },
};

const STATUS_OPTIONS: { value: string }[] = [
  { value: AlertStatus.Pending },
  { value: AlertStatus.Resolved },
  { value: AlertStatus.Ignored },
];

/* --------------------- server-side filter controls --------------------- */

const TYPE_OPTIONS: { label: string; value: string }[] = ALERT_TYPES.map((type) => ({
  label: alertTypeLabel(type),
  value: type,
}));

// Slider works in whole percent (0–100); the API takes 0–1 fractions.
const CONFIDENCE_PERCENT_RANGE: readonly [number, number] = [0, 100];
const CONFIDENCE_PERCENT_STEP = 5;

const normalizeArray = (value?: string[]) => {
  if (!value || value.length === 0) return undefined;
  const seen = new Set<string>();
  const normalized = value.filter((item) => {
    if (!item || seen.has(item)) return false;
    seen.add(item);
    return true;
  });
  return normalized.length > 0 ? normalized : undefined;
};

/** Order-insensitive array equality (both sides normalized first). */
const areArraysEqual = (left?: string[], right?: string[]) => {
  const normalizedLeft = normalizeArray(left) ?? [];
  const normalizedRight = normalizeArray(right) ?? [];
  if (normalizedLeft.length !== normalizedRight.length) return false;
  const rightSet = new Set(normalizedRight);
  return normalizedLeft.every((item) => rightSet.has(item));
};

const toggleValue = (current: string[], value: string, checked: boolean) => {
  if (checked) return current.includes(value) ? current : [...current, value];
  return current.filter((item) => item !== value);
};

/** Current confidence filter as a percent pair for the slider (defaults to full range). */
const confidenceToPercent = (filters: LocationAlertFilters): [number, number] => [
  Math.round((filters.min_confidence ?? 0) * 100),
  Math.round((filters.max_confidence ?? 1) * 100),
];

const hasConfidenceFilter = (filters: LocationAlertFilters) =>
  filters.min_confidence !== undefined || filters.max_confidence !== undefined;

/**
 * Compact multiselect dropdown (Type / Label). Edits a local draft while open
 * and commits on close — and only when the selection actually changed
 * (order-insensitive), so the server-side queries don't refetch needlessly.
 */
const FeedMultiSelect = ({
  label,
  options,
  value,
  onCommit,
}: {
  label: string;
  options: { label: string; value: string }[];
  value?: string[];
  onCommit: (next: string[] | undefined) => void;
}) => {
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<string[]>(value ?? []);

  useEffect(() => {
    if (open) setDraft(value ?? []);
  }, [open, value]);

  const active = normalizeArray(value);

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      const normalizedDraft = normalizeArray(draft);
      if (!areArraysEqual(normalizedDraft, active)) onCommit(normalizedDraft);
    }
    setOpen(next);
  };

  return (
    <DropdownMenu open={open} onOpenChange={handleOpenChange}>
      <DropdownMenuTrigger asChild>
        <Button
          size="sm"
          variant="outline"
          className={cn(
            "h-8 gap-1 text-xs",
            active && "border-primary/50 bg-primary/5 text-primary hover:bg-primary/10 hover:text-primary",
          )}
        >
          {label}
          {active ? ` (${active.length})` : ""}
          <ChevronDown className="h-3.5 w-3.5 shrink-0" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="start" className="max-h-72 w-56 overflow-y-auto">
        {options.map((option) => (
          <DropdownMenuCheckboxItem
            key={option.value}
            checked={draft.includes(option.value)}
            onSelect={(e) => e.preventDefault()}
            onCheckedChange={(checked) =>
              setDraft((current) => toggleValue(current, option.value, checked === true))
            }
          >
            {option.label}
          </DropdownMenuCheckboxItem>
        ))}
      </DropdownMenuContent>
    </DropdownMenu>
  );
};

/**
 * Status filter as a row of clickable colored pill toggles (one per status).
 * Clicking a pill toggles that status in/out of the selection; multiple can be
 * active. Empty selection = no status constraint (all shown) — same semantics
 * as the previous multi-select dropdown, just a different control.
 */
const StatusPills = ({
  value,
  onChange,
}: {
  value: AlertStatus[];
  onChange: (next: AlertStatus[]) => void;
}) => {
  const { t } = useTranslation("map");
  const toggle = (status: AlertStatus) =>
    onChange(value.includes(status) ? value.filter((s) => s !== status) : [...value, status]);

  return (
    <div className="flex items-center gap-1.5">
      {STATUS_OPTIONS.map(({ value: optionValue }) => {
        const status = optionValue as AlertStatus;
        const s = statusBadge[status];
        const selected = value.includes(status);
        return (
          <button
            key={status}
            type="button"
            aria-pressed={selected}
            onClick={() => toggle(status)}
            className={cn(
              "inline-flex h-8 items-center gap-1.5 rounded-full border px-3 text-xs font-semibold transition-colors",
              selected
                ? cn(s.solid, "border-transparent")
                : cn(s.pill, "border-input opacity-70 hover:opacity-100"),
            )}
          >
            <span className={cn("h-1.5 w-1.5 rounded-full", selected ? "bg-white/80" : s.dot)} />
            {t(`alertStatus.${s.labelKey}`)}
          </button>
        );
      })}
    </div>
  );
};

const StatusBadge = ({ t, status }: { t: TFunction<"map">; status: AlertStatus }) => {
  const s = statusBadge[status] ?? statusBadge[AlertStatus.Pending];
  return (
    <span
      className={cn(
        "inline-flex shrink-0 items-center gap-1.5 rounded-full px-2 py-0.5 text-xs font-semibold",
        s.pill,
      )}
    >
      <span className={cn("h-1.5 w-1.5 rounded-full", s.dot)} />
      {t(`alertStatus.${s.labelKey}`)}
    </span>
  );
};

interface MapAlertsFeedProps {
  alerts: Alert[];
  machineNameByDevice: Record<string, string>;
  isLoading: boolean;
  /** Currently-selected alert id (drives row highlight + the on-map card). */
  selectedAlertId: string | null;
  /** Clicking a row selects the alert → opens the on-map alert card. */
  onSelectAlert: (id: number) => void;
  /**
   * Status filter (multi-select) — controlled by the page so it also drives the
   * map markers. Empty array means no status constraint (all statuses).
   */
  status: AlertStatus[];
  onStatusChange: (status: AlertStatus[]) => void;
  /**
   * Server-side filters (type/label/confidence) — controlled by the page like
   * `status`, so they also drive the map markers and the notify flow.
   */
  filters: LocationAlertFilters;
  onFiltersChange: (next: LocationAlertFilters) => void;
  /** Ids of freshly-arrived (unseen) alerts — highlighted as "new" in the list. */
  newIds?: Set<number>;
  /**
   * Alert id currently emphasized (hovered here or on the map, else the
   * selection) — all other rows fade to half opacity.
   */
  emphasizedAlertId?: string | null;
  /** Row hover enter/leave — mirrors list hover onto the map markers. */
  onHoverAlert?: (id: string | null) => void;
}

export function MapAlertsFeed({
  alerts,
  machineNameByDevice,
  isLoading,
  selectedAlertId,
  onSelectAlert,
  status,
  onStatusChange,
  filters,
  onFiltersChange,
  newIds,
  emphasizedAlertId,
  onHoverAlert,
}: MapAlertsFeedProps) {
  const { t } = useTranslation("map");
  const [machines, setMachines] = useState<string[]>([]);
  const [search, setSearch] = useState("");
  // Ordering is purely client-side (the queries stay newest-first so the
  // 1000-item cap keeps fresh alerts for the notify flow).
  const [ordering, setOrdering] = useState<"newest" | "oldest">("newest");

  const machineOptions = useMemo(
    () => Object.entries(machineNameByDevice).map(([device, name]) => ({ device, name })),
    [machineNameByDevice],
  );

  // Label options from the classification taxonomy (canonical value, display name).
  const { data: taxonomy } = useGetClassificationTaxonomy();
  const labelOptions = useMemo(() => {
    const seen = new Set<string>();
    return (
      taxonomy?.families.flatMap((family) =>
        family.members.flatMap((member) => {
          if (seen.has(member.canonical)) return [];
          seen.add(member.canonical);
          return [{ label: member.display_name, value: member.canonical }];
        }),
      ) ?? []
    );
  }, [taxonomy]);

  // Merge a patch into the controlled filters, dropping cleared (undefined)
  // fields so a fully-reset state is exactly `{}`.
  const patchFilters = (patch: Partial<LocationAlertFilters>) => {
    const next: LocationAlertFilters = { ...filters, ...patch };
    (Object.keys(next) as (keyof LocationAlertFilters)[]).forEach((key) => {
      if (next[key] === undefined) delete next[key];
    });
    onFiltersChange(next);
  };

  // Confidence popover — drafts in percent while open, commits on close (only
  // when changed); the full 0–100% range means "no filter".
  const [confidenceOpen, setConfidenceOpen] = useState(false);
  const [confidenceDraft, setConfidenceDraft] = useState<[number, number]>(
    confidenceToPercent(filters),
  );
  const confidenceActive = hasConfidenceFilter(filters);
  const handleConfidenceOpenChange = (next: boolean) => {
    if (next) {
      setConfidenceDraft(confidenceToPercent(filters));
    } else {
      const [min, max] = confidenceDraft;
      const nextMin =
        min <= CONFIDENCE_PERCENT_RANGE[0] && max >= CONFIDENCE_PERCENT_RANGE[1]
          ? undefined
          : Math.min(min, max) / 100;
      const nextMax =
        min <= CONFIDENCE_PERCENT_RANGE[0] && max >= CONFIDENCE_PERCENT_RANGE[1]
          ? undefined
          : Math.max(min, max) / 100;
      if (nextMin !== filters.min_confidence || nextMax !== filters.max_confidence) {
        patchFilters({ min_confidence: nextMin, max_confidence: nextMax });
      }
    }
    setConfidenceOpen(next);
  };

  const hasServerFilters =
    !!normalizeArray(filters.type_in) || !!normalizeArray(filters.label_in) || confidenceActive;

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const list = alerts.filter((a) => {
      if (status.length > 0 && !status.includes(a.status)) return false;
      if (machines.length > 0 && !machines.includes(a.device_mxid)) return false;
      if (q && !a.type.toLowerCase().includes(q)) return false;
      return true;
    });
    // The source list is newest-first; "Oldest" flips it client-side.
    if (ordering === "oldest") {
      list.sort((a, b) => new Date(a.timestamp).getTime() - new Date(b.timestamp).getTime());
    }
    return list;
  }, [alerts, status, machines, search, ordering]);

  const toggleMachine = (device: string) =>
    setMachines((prev) =>
      prev.includes(device) ? prev.filter((d) => d !== device) : [...prev, device],
    );

  // --- windowing ---
  const scrollRef = useRef<HTMLDivElement>(null);
  const [scrollTop, setScrollTop] = useState(0);
  const [viewportHeight, setViewportHeight] = useState(0);

  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const ro = new ResizeObserver(() => setViewportHeight(el.clientHeight));
    ro.observe(el);
    setViewportHeight(el.clientHeight);
    return () => ro.disconnect();
  }, []);

  // Reset scroll to top when the filter result set changes meaningfully.
  useEffect(() => {
    scrollRef.current?.scrollTo?.({ top: 0 });
    setScrollTop(0);
  }, [
    status,
    machines,
    search,
    ordering,
    filters.type_in,
    filters.label_in,
    filters.min_confidence,
    filters.max_confidence,
  ]);

  const total = filtered.length;
  const startIndex = Math.max(0, Math.floor(scrollTop / ROW_HEIGHT) - OVERSCAN);
  const endIndex = Math.min(
    total,
    Math.ceil((scrollTop + viewportHeight) / ROW_HEIGHT) + OVERSCAN,
  );
  const visible = filtered.slice(startIndex, endIndex);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3">
      {/* Filter bar */}
      <div className="flex flex-wrap items-center gap-2">
        <StatusPills value={status} onChange={onStatusChange} />

        {machineOptions.length > 1 && (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button size="sm" variant="outline" className="h-8 text-xs">
                {t("alertsFeed.machines")}
                {machines.length > 0 ? ` (${formatNumber(machines.length)})` : ""}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="start">
              {machineOptions.map(({ device, name }) => (
                <DropdownMenuCheckboxItem
                  key={device}
                  checked={machines.includes(device)}
                  onCheckedChange={() => toggleMachine(device)}
                  onSelect={(e) => e.preventDefault()}
                >
                  {name}
                </DropdownMenuCheckboxItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        )}

        <FeedMultiSelect
          label={t("alertsFeed.type")}
          options={TYPE_OPTIONS}
          value={filters.type_in}
          onCommit={(type_in) => patchFilters({ type_in })}
        />

        <FeedMultiSelect
          label={t("alertsFeed.label")}
          options={labelOptions}
          value={filters.label_in}
          onCommit={(label_in) => patchFilters({ label_in })}
        />

        <Popover open={confidenceOpen} onOpenChange={handleConfidenceOpenChange}>
          <PopoverTrigger asChild>
            <Button
              size="sm"
              variant="outline"
              className={cn(
                "h-8 gap-1 text-xs",
                confidenceActive &&
                  "border-primary/50 bg-primary/5 text-primary hover:bg-primary/10 hover:text-primary",
              )}
            >
              {confidenceActive
                ? t("alertsFeed.confidenceShort", {
                    min: Math.round((filters.min_confidence ?? 0) * 100),
                    max: Math.round((filters.max_confidence ?? 1) * 100),
                  })
                : t("alertsFeed.confidence")}
              <ChevronDown className="h-3.5 w-3.5 shrink-0" />
            </Button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-64 space-y-3 p-4">
            <div className="flex items-center justify-between gap-2">
              <div className="text-xs font-medium">
                {t("alertsFeed.confidenceRange", {
                  min: confidenceDraft[0],
                  max: confidenceDraft[1],
                })}
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="h-7 px-2 text-xs"
                onClick={() => setConfidenceDraft([...CONFIDENCE_PERCENT_RANGE])}
              >
                {t("alertsFeed.reset")}
              </Button>
            </div>
            <SliderPrimitive.Root
              value={confidenceDraft}
              min={CONFIDENCE_PERCENT_RANGE[0]}
              max={CONFIDENCE_PERCENT_RANGE[1]}
              step={CONFIDENCE_PERCENT_STEP}
              minStepsBetweenThumbs={1}
              onValueChange={(next) => setConfidenceDraft([next[0], next[1]] as [number, number])}
              className="relative flex w-full touch-none select-none items-center"
            >
              <SliderPrimitive.Track className="relative h-1.5 w-full grow overflow-hidden rounded-full bg-secondary">
                <SliderPrimitive.Range className="absolute h-full bg-primary" />
              </SliderPrimitive.Track>
              {confidenceDraft.map((_, index) => (
                <SliderPrimitive.Thumb
                  // Stable index key — keying by value breaks thumb focus mid-drag.
                  key={index}
                  className="block h-4 w-4 rounded-full border-2 border-primary bg-background ring-offset-background transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2"
                />
              ))}
            </SliderPrimitive.Root>
            <div className="flex justify-between text-[10px] text-muted-foreground">
              <span>0%</span>
              <span>100%</span>
            </div>
          </PopoverContent>
        </Popover>

        <div className="inline-flex h-8 items-center rounded-md border p-0.5">
          {(["newest", "oldest"] as const).map((option) => (
            <Button
              key={option}
              size="sm"
              variant={ordering === option ? "secondary" : "ghost"}
              className="h-7 px-2 text-xs capitalize"
              onClick={() => setOrdering(option)}
            >
              {option === "newest" ? t("alertsFeed.newest") : t("alertsFeed.oldest")}
            </Button>
          ))}
        </div>

        {hasServerFilters && (
          <Button
            size="sm"
            variant="ghost"
            className="h-8 px-2 text-xs"
            onClick={() => onFiltersChange({})}
            aria-label={t("alertsFeed.clearFilters")}
            title={t("alertsFeed.clearFilters")}
          >
            <X className="h-3.5 w-3.5" />
          </Button>
        )}

        <Input
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder={t("alertsFeed.searchPlaceholder")}
          className="h-8 flex-1 min-w-[120px] text-sm"
        />
      </div>

      {/* Count */}
      {!isLoading && total > 0 && (
        <div className="px-0.5 text-xs text-muted-foreground">
          {t("alertsFeed.count", { count: total, total: formatNumber(total) })}
        </div>
      )}

      {/* Windowed feed */}
      <div
        ref={scrollRef}
        onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
        className="-mx-4 min-h-0 flex-1 overflow-y-auto border-t"
      >
        {isLoading && total === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">{t("alertsFeed.loading")}</div>
        ) : total === 0 ? (
          <div className="p-6 text-center text-sm text-muted-foreground">
            {t("alertsFeed.empty")}
          </div>
        ) : (
          <div style={{ height: total * ROW_HEIGHT, position: "relative" }}>
            {visible.map((alert, i) => {
              const index = startIndex + i;
              const isSelected = String(alert.id) === selectedAlertId;
              const isNew = newIds?.has(alert.id) ?? false;
              const isDimmed =
                emphasizedAlertId != null && String(alert.id) !== emphasizedAlertId;
              return (
                <div
                  key={alert.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => onSelectAlert(alert.id)}
                  onMouseEnter={onHoverAlert ? () => onHoverAlert(String(alert.id)) : undefined}
                  onMouseLeave={onHoverAlert ? () => onHoverAlert(null) : undefined}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      onSelectAlert(alert.id);
                    }
                  }}
                  style={{ position: "absolute", top: index * ROW_HEIGHT, left: 0, right: 0, height: ROW_HEIGHT }}
                  className={cn(
                    "flex cursor-pointer items-center gap-2 border-b border-l-2 px-4 transition-[background-color,opacity]",
                    isDimmed && "opacity-50",
                    isSelected
                      ? // Blue-600 selected accent — matches the on-map selected marker ring.
                        "border-l-blue-600 bg-blue-600/10 ring-1 ring-inset ring-blue-600/50"
                      : isNew
                        ? "border-l-red-500 bg-red-500/10 hover:bg-red-500/15"
                        : "border-l-transparent hover:bg-muted/50",
                  )}
                >
                  {isNew && (
                    <span className="shrink-0 rounded bg-red-500 px-1 py-0.5 text-[9px] font-bold uppercase leading-none text-white">
                      {t("alertsFeed.new")}
                    </span>
                  )}
                  <StatusBadge t={t} status={alert.status} />
                  <span className="shrink-0 rounded bg-muted px-1.5 py-0.5 text-xs font-medium text-muted-foreground">
                    {machineNameByDevice[alert.device_mxid] ?? alert.device_mxid}
                  </span>
                  <span className="min-w-0 flex-1 truncate text-sm">{alertTypeLabel(alert.type)}</span>
                  <span className="shrink-0 text-xs text-muted-foreground">
                    {formatRelativeTime(alert.timestamp)}
                  </span>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
